"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useAirAggregation = void 0;
const react_1 = require("react");
const useAlertChangeRefresh_1 = require("./useAlertChangeRefresh");
const queries_1 = require("../../graphql/queries");
const air_1 = require("../../utils/aggregation/air");
const alertStatus_1 = require("../../utils/alertStatus");
const graphqlCache_1 = require("../../utils/graphqlCache");
const sensorClassification_1 = require("../../utils/sensorClassification");
const stableValue_1 = require("../../utils/stableValue");
const POLLING_INTERVAL_MS = 30_000;
const useAirAggregation = (hubId, allSensors) => {
    const sensors = allSensors ?? [];
    const [fetchResults, setFetchResults] = (0, react_1.useState)(new Map());
    const [loading, setLoading] = (0, react_1.useState)(true);
    const lastFetchTime = (0, react_1.useRef)(0);
    const intervalRef = (0, react_1.useRef)(null);
    const airSensors = (0, react_1.useMemo)(() => (0, air_1.selectAirSensors)(sensors), [sensors, hubId]);
    const fetchAllSensorData = (0, react_1.useCallback)(async (force = false) => {
        if (!hubId || airSensors.length === 0) {
            setFetchResults(new Map());
            setLoading(false);
            return;
        }
        const now = Date.now();
        if (!force && now - lastFetchTime.current < 5000)
            return;
        lastFetchTime.current = now;
        if (force)
            (0, graphqlCache_1.clearGraphqlCache)();
        try {
            const results = await Promise.all(airSensors.map(async (sensor) => {
                try {
                    const [eventResponse, alertResponse] = await Promise.all([
                        (0, graphqlCache_1.cachedGraphql)({
                            query: queries_1.getSensorEventsBySensor,
                            variables: { sensorId: sensor.id, limit: 5 },
                        }),
                        (0, graphqlCache_1.cachedGraphql)({
                            query: queries_1.getSensorAlertsBySensor,
                            variables: { sensorId: sensor.id, limit: 20 },
                        }),
                    ]);
                    const events = eventResponse.data?.getSensorEventsBySensor?.items ||
                        eventResponse.data?.getSensorEventsBySensor ||
                        [];
                    // Find the first DATA event (skip battery-only events that lack values)
                    const dataEvent = events.find((e) => e.values);
                    const alerts = alertResponse.data?.getSensorAlertsBySensor?.items ||
                        alertResponse.data?.getSensorAlertsBySensor ||
                        [];
                    return {
                        sensorId: sensor.id,
                        event: dataEvent ? (0, sensorClassification_1.parseSensorEvent)(dataEvent) : null,
                        alerts: (0, alertStatus_1.activeAlerts)(alerts),
                    };
                }
                catch {
                    return { sensorId: sensor.id, event: null, alerts: [] };
                }
            }));
            const map = new Map();
            results.forEach((r) => map.set(r.sensorId, r));
            setFetchResults((prev) => (0, stableValue_1.stable)(prev, map));
        }
        catch {
            // Keep existing results
        }
        finally {
            setLoading(false);
        }
    }, [hubId, airSensors.map((s) => s.id).join(',')]);
    (0, react_1.useEffect)(() => {
        fetchAllSensorData();
        intervalRef.current = setInterval(() => fetchAllSensorData(), POLLING_INTERVAL_MS);
        return () => {
            if (intervalRef.current)
                clearInterval(intervalRef.current);
        };
    }, [fetchAllSensorData]);
    const refreshOnAlertChange = (0, react_1.useCallback)(() => {
        fetchAllSensorData(true);
    }, [fetchAllSensorData]);
    (0, useAlertChangeRefresh_1.useAlertChangeRefresh)(refreshOnAlertChange);
    return (0, react_1.useMemo)(() => (0, air_1.aggregateAirSensors)(airSensors, fetchResults, loading), [airSensors, fetchResults, loading]);
};
exports.useAirAggregation = useAirAggregation;
